<template>
  <h3>Mis gastos de saldo</h3>

  <div class="controlls">
    <a-form class="form" layout="vertical" :model="formState">
      <a-form-item>
        <ChannelIdMultiPicker
          v-model:value="formState.channel_customers"
          :channelType="ChannelType.Whatsapp"
          includeDeleted
          @select-with-deleted="handleSelectChannelCustomers"
        />
      </a-form-item>

      <a-form-item>
        <a-tooltip title="Puedes seleccionar un rango máximo de 31 días.">
          <!-- TODO: (observaciones beex) Cambiar linea :disabled-date="disabledDateRage && disabledDateRule" por :disabled-date="disabledDateRage" -->
          <a-range-picker
            v-model:value="formState.dateRange"
            format="DD/MM/YYYY"
            valueFormat="YYYY-MM-DD"
            :disabled-date="disabledDateRage"
            @calendarChange="handleCalendarChange"
          />
        </a-tooltip>
      </a-form-item>
    </a-form>

    <a-button :loading="downloading" @click="handleDownload"
      >Descargar gastos <DownloadOutlined
    /></a-button>
  </div>

  <a-alert 
    v-if="hasItemsDeleted > 0"
    style="margin-bottom: 12px"
    message="Algunos identificadores fueron eliminados, pero registran consumos dentro del período seleccionado." 
    type="warning" 
    show-icon 
  />

  <a-table
    size="middle"
    id="expenses-table"
    :scroll="scrollStyle"
    :loading="loading"
    :columns="expensesTableColumns"
    :dataSource="expensesCurrentPage"
    :pagination="false"
    @change="handleTableChange"
  >
    <template #headerCell="{ title, column }">
      <template v-if="column.type === 'info'">
        {{ title }}

        <a-tooltip :title="column.info">
          <InfoCircleOutlined />
        </a-tooltip>
      </template>
    </template>

    <template #bodyCell="{ column, record, text, index }">
      <template v-if="column.dataIndex === 'date'">
        {{ dateTimeToSlashFormat(text, false) }}
      </template>

      <template v-if="column.dataIndex === 'total_session_price'">
        ${{ Number(text ?? 0).toFixed(4) }}

        <a-tooltip
          v-if="text >= 200"
          title="Pasando $200, para todos los mensajes, incluyendo plantillas, ya no se cobra el fee, solo se cobra el costo de META."
        >
          <InfoCircleOutlined style="margin-left: 5px" />
        </a-tooltip>
      </template>

      <template v-if="column.dataIndex === 'identifier'">
        {{ record.channel_customer?.alias || record.channel_customer?.associate }} ({{
          record.channel_customer?.associate
        }})
      </template>

      <template v-if="column.dataIndex === 'status'">
        <a-tooltip
          style="z-index: 9999"
        >
          <template #title v-if="record.channel_customer?.status === StatusIdentifier.delete && record.channel_customer?.deleted_at">
            Identificador eliminado el <br/>{{ dateToString(record.channel_customer?.deleted_at, 'DD/MM/YYYY [a las] HH:mm') }}
          </template>
          <div>
            <a-tag :color="getStatusColor(record.channel_customer?.status)">
              {{ getStatusText(record.channel_customer?.status) }}
              <InfoCircleOutlined v-if="record.channel_customer?.status === StatusIdentifier.delete" />
            </a-tag>
          </div>
        </a-tooltip>
      </template>

      <template v-if="index === tableSource.length - 1 && column.dataIndex === 'date'">
        <div ref="observerFlagRef" />
      </template>
    </template>

    <template #summary>
      <a-table-summary fixed>
        <a-table-summary-row>
          <a-table-summary-cell>Total</a-table-summary-cell>

          <a-table-summary-cell> </a-table-summary-cell>

          <a-table-summary-cell> </a-table-summary-cell>

          <a-table-summary-cell> </a-table-summary-cell>

          <a-table-summary-cell> </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("session_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell> </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("fep_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("service_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("marketing_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("marketing_lite_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("authentication_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell>
            {{ getTotalByCategory("utility_quantity") }}
          </a-table-summary-cell>

          <a-table-summary-cell> ${{ getTotalByCategory("total_price") }} </a-table-summary-cell>
        </a-table-summary-row>
      </a-table-summary>
    </template>
  </a-table>
</template>

<script setup>
import { ref, reactive, watch, computed } from "vue";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons-vue";
import { useIntersectionObserver } from "@vueuse/core";
import _ from "lodash";
import dayjs from "dayjs";

import { expensesTableColumns } from "@/app/settings/utils/columns";
import { fetchWhatsappExpenses, downloadExpenses } from "@/app/settings/services";
import { useScrollResize } from "@/app/shared/composables/useScrollResize";
import { dateTimeToSlashFormat, dateToString } from "@/app/shared/utils/dates";
import { ChannelType } from "@/app/shared/utils/enums";
import { downloadFile } from "@/app/shared/utils/file";
import { useDateRangeLimit } from "@/app/shared/composables/useDateRangeLimit";

import ChannelIdMultiPicker from "@/app/shared/components/pickers/ChannelIdMultiPicker.vue";

const { scrollStyle } = useScrollResize({ headerMargin: 525, width: 1800 });

const emit = defineEmits(["loading"]);

const observerFlagRef = ref(null);

// TODO: (observaciones beex) Eliminar (observaciones beex)
// const disabledDateRule = (current) => {
//   const workspace = localStorage.getItem("workspace");
//   if (workspace != "culqi") return current && current < new Date(2024, 7, 1);
//   return false;
// };

const { pause, resume } = useIntersectionObserver(observerFlagRef, ([{ isIntersecting }]) => {
  console.log({ isIntersecting });
  if (isIntersecting && expensesCurrentPage.value.next) {
    currentTablePage.value++;
    fetchExpensesTableData({ resetFilters: false });
  }
});

const todayDate = dayjs().format("YYYY-MM-DD");

const formState = reactive({
  billing_category: "",
  dateRange: [todayDate, todayDate],
  channel_customers: [],
});
const channelCustomersWithDeleted = ref([]);

const expensesCurrentPage = ref([]);

const loading = ref(false);
const currentTablePage = ref(1);

const tableSource = ref([]);
// const totalExpenses = ref(null);

const StatusIdentifier = {
  active: 1,
  inactive: 2,
  delete: 3,
}

const handleSelectChannelCustomers = (channelCustomerIds) => {
  channelCustomersWithDeleted.value = channelCustomerIds;
};

const hasItemsDeleted = computed(() => {
  return expensesCurrentPage.value?.some((record) => record.channel_customer?.status === StatusIdentifier.delete);
});

const getStatusText = (status) => {
  let value;
  switch (status) {
    case StatusIdentifier.active:
      value = "Activo";
      break;
    case StatusIdentifier.inactive:
      value = "Inactivo";
      break;
    case StatusIdentifier.delete:
      value = "Eliminado";
      break;
  }
  return value;
};

const getStatusColor = (status) => {
  let value;
  switch (status) {
    case StatusIdentifier.active:
      value = "green";
      break;
    case StatusIdentifier.inactive:
      value = "default";
      break;
    case StatusIdentifier.delete:
      value = "red";
      break;
  }
  return value;
};

const { handleCalendarChange, disabledDateRage } = useDateRangeLimit(31);

const getTotalByCategory = (category) => {
  const categoryData = expensesCurrentPage.value.map((record) => record[category]);

  const totalSum = categoryData.reduce((prev, curr) => prev + curr, 0);

  return Number(totalSum.toFixed(4));
};

const fetchExpensesTableData = async ({ resetFilters = true } = {}) => {
  if (resetFilters) loading.value = true;

  if (!resetFilters) pause();

  const filters = _.cloneDeep(formState);

  if (!filters.dateRange || filters.dateRange.length < 2) {
    expensesCurrentPage.value = [];
    loading.value = false;
    return;
  }

  filters.dateRange[1] = `${filters.dateRange[1]} 23:59:59`;

  emit('loading', true);
  try {
    expensesCurrentPage.value = await fetchWhatsappExpenses({
      filters: {
        ...filters,
        channel_customers: channelCustomersWithDeleted.value
      },
      page: currentTablePage.value,
    });
  }
  finally {
    emit('loading', false);
  }

  // totalExpenses.value = await fetchTotalExpenses({ filters: filters });

  // if (resetFilters) tableSource.value = [];

  // tableSource.value.push(...expensesCurrentPage.value.results);
  //
  // if (!resetFilters) resume();
  //
  loading.value = false;
};

// const disabledDate = (current) => {
//   const limit = dayjs("2023-06-30");
//
//   return current && current < limit.endOf("day");
// };

const downloading = ref(false);

const handleDownload = async () => {
  downloading.value = true;

  try {
    const filters = _.cloneDeep(formState);
    filters.dateRange[1] = `${filters.dateRange[1]} 23:59:59`;

    const response = await downloadExpenses({ filters });

    downloadFile(response, "expenses.xlsx", { useResponseFileName: true });
  } catch (error) {
    console.error(error);
  }

  downloading.value = false;
};

watch(
  formState,
  _.debounce(() => {
    currentTablePage.value = 1;
    fetchExpensesTableData();
  }, 500),
  { immediate: true }
);

defineExpose({ fetchExpensesTableData });
</script>

<style lang="sass" scoped>
.controlls
  margin-bottom: 12px
  display: flex
  align-items: center
  justify-content: space-between

.form
  display: flex
  gap: 12px

  .input
    min-width: 160px

:deep(.ant-form-item)
  margin: 0

#expenses-table tfoot th,
#expenses-table tfoot td
  background-color: #fafafa !important
</style>
